import { useCallback, useEffect, useState } from "react";
import { useGetAllSchools } from "../../../Dashboard";
import SchoolCard from "./SchoolsCard";
import Select from "react-select";
import { IoIosArrowDown } from "react-icons/io";
import LoadingIcon from "../../../../../assets/loading-purple.svg";
import SearchFilterBar from "../components/SearchFilterBar";
import { useSearchParams } from "react-router-dom";

type FilterOption = {
  value: string;
  name: string;
};

type FiltersState = {
  trustee: FilterOption | null;
  institute: FilterOption | null;
};

type RowsPerPageSelectProps = {
  setItemsPerRow: (value: number) => void;
  itemsPerRow: number;
  className?: string;
};

export function RowsPerPageSelect({
  setItemsPerRow,
  itemsPerRow,
  className,
}: RowsPerPageSelectProps) {
  return (
    <div
      className={
        "flex gap-x-2 text-xs items-center px-4 py-0 " +
        (className ? className : " justify-end")
      }
    >
      <p className="shrink-0">Rows per page:</p>

      <Select
        className="w-24"
        options={[10, 50, 150, 500].map((num) => ({
          label: num.toString(),
          value: num,
        }))}
        isSearchable={false}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: () => null,
        }}
        onChange={(option: any) => {
          setItemsPerRow(option.value);
        }}
        value={{ label: itemsPerRow.toString(), value: itemsPerRow }}
        styles={{
          control: (provided) => ({
            ...provided,
            backgroundColor: "white",
            height: "30px",
            minHeight: "30px",
            border: "1px solid #e5e7eb",
            boxShadow: "none",
            cursor: "pointer",
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: "30px",
            padding: "0 8px",
          }),
          input: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: "12px",
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: "30px",
          }),
          option: (provided) => ({
            ...provided,
            fontSize: "12px",
            cursor: "pointer",
          }),
        }}
      />
    </div>
  );
}

export const CustomDropdownIndicator = () => {
  return (
    <div>
      <IoIosArrowDown className="text-xs w-8 text-[#1E1B59]" />
    </div>
  );
};

export const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-8">
    <img src={LoadingIcon} alt="Loading..." className="w-8 h-8 animate-spin" />
  </div>
);

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const renderPaginationButtons = () => {
    const buttons: any[] = [];
    let startPage: number, endPage: number;

    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 3) {
        startPage = 1;
        endPage = 5;
      } else if (currentPage + 2 >= totalPages) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`px-4 py-2 mx-1 min-w-[40px] rounded-lg transition-colors ${
            i === currentPage
              ? "bg-indigo-500 text-white text-sm font-medium shadow-sm"
              : "text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50"
          }`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }
    if (totalPages > 5) {
      if (currentPage + 1 > 5) {
        buttons.unshift(
          <span key="ellipsis2" className="px-2">
            ...
          </span>
        );
      }

      if (currentPage + 1 > 5) {
        buttons.unshift(
          <button
            key="first"
            className="px-4 py-2 mx-1 min-w-[40px] rounded-lg text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50 transition-colors"
            onClick={() => onPageChange(1)}
          >
            {1}
          </button>
        );
      }

      if (currentPage > 3) {
        buttons.unshift(
          <button
            key="prev"
            className="px-3 py-2 mx-1 rounded-lg text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center"
            onClick={() => onPageChange(currentPage - 1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </button>
        );
      }

      if (currentPage + 3 < totalPages) {
        buttons.push(
          <span key="ellipsis1" className="px-2">
            ...
          </span>
        );
      }

      if (currentPage + 3 < totalPages) {
        buttons.push(
          <button
            key="last"
            className="px-4 py-2 mx-1 min-w-[40px] rounded-lg text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50 transition-colors"
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }

      if (currentPage + 2 < totalPages) {
        buttons.push(
          <button
            key="next"
            className="px-3 py-2 mx-1 rounded-lg text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center"
            onClick={() => onPageChange(currentPage + 1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        );
      }
    } else {
      if (currentPage > 1) {
        buttons.unshift(
          <button
            key="prev"
            className="px-3 py-2 mx-1 rounded-lg text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center"
            onClick={() => onPageChange(currentPage - 1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </button>
        );
      }

      if (currentPage < totalPages) {
        buttons.push(
          <button
            key="next"
            className="px-3 py-2 mx-1 rounded-lg text-[#1B163B] text-sm font-normal border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center"
            onClick={() => onPageChange(currentPage + 1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        );
      }
    }

    return buttons;
  };

  return (
    <div className="flex items-center justify-center">
      {renderPaginationButtons()}
    </div>
  );
}

function SchoolList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: schoolsData,
    loading: schoolsLoading,
    error: schoolsError,
    refetch: refetchSchoolsQuery,
  } = useGetAllSchools();

  const initialPage = parseInt(searchParams.get("page") || "1");
  const initialItemsPerRow = parseInt(searchParams.get("limit") || "10");
  const initialSearchText = searchParams.get("search") || "";
  const initialSearchFilter = searchParams.get("filter") || "school_id";

  const [searchText, setSearchText] = useState(initialSearchText);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerRow, setItemsPerRow] = useState(initialItemsPerRow);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSchoolData, setLocalSchoolData] = useState<any>(null);

  const [filters, setFilters] = useState<FiltersState>({
    trustee: null,
    institute: null,
  });

  const buildRefetchParams = useCallback(
    (page: number) => {
      const params: any = {
        page,
        limit: itemsPerRow,
      };

      if (searchText.trim()) {
        if (searchFilter === "school_id") params.school_id = searchText.trim();
        if (searchFilter === "school_name")
          params.school_name = searchText.trim();
        if (searchFilter === "trustee_id")
          params.trustee_id = searchText.trim();
      }

      if (filters?.trustee && searchFilter !== "trustee_id") {
        params.trustee_id = filters.trustee.value;
      }

      if (filters?.institute && searchFilter !== "school_id") {
        params.school_id = filters.institute.value;
      }

      return params;
    },
    [filters, itemsPerRow, searchFilter, searchText]
  );

  useEffect(() => {
    if (schoolsData?.getallschool) {
      setLocalSchoolData(schoolsData.getallschool);
    }
  }, [schoolsData]);

  useEffect(() => {
    const params: any = {
      page: currentPage.toString(),
      limit: itemsPerRow.toString(),
    };

    if (searchText) {
      params.search = searchText;
      params.filter = searchFilter;
    }

    setSearchParams(params);
  }, [currentPage, itemsPerRow, searchText, searchFilter, setSearchParams]);

  const refetchSchools = async (params?: any) => {
    try {
      setRefetchLoading(true);
      const result = await refetchSchoolsQuery(params || {});
      if (result?.data?.getAllSchools) {
        setLocalSchoolData(result.data.getAllSchools);
      }
      return result;
    } finally {
      setRefetchLoading(false);
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    try {
      const params = buildRefetchParams(page);
      await refetchSchools(params);
    } finally {
      setLoading(false);
    }
  };

  const isLoading =
    schoolsLoading || loading || refetchLoading || !localSchoolData;

  const hasNoData =
    !localSchoolData ||
    !localSchoolData.data ||
    localSchoolData.data.length === 0;

  if (schoolsError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading school details
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">School Profiles</h1>
      </div>

      <SearchFilterBar
        refetchSchools={refetchSchools}
        itemsPerRow={itemsPerRow}
        setItemsPerRow={setItemsPerRow}
        setCurrentPage={setCurrentPage}
        searchText={searchText}
        setSearchText={setSearchText}
        searchFilter={searchFilter}
        setSearchFilter={setSearchFilter}
        filters={filters}
        setFilters={setFilters}
        setLoading={setLoading}
        refetchLoading={refetchLoading}
        setRefetchLoading={setRefetchLoading}
      />

      {isLoading ? (
        <LoadingSpinner />
      ) : hasNoData ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-xl text-gray-500 mb-4">No schools found</div>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {localSchoolData.data.map((school: any, index: number) => (
              <SchoolCard key={school._id || index} school={school} />
            ))}
          </div>

          {(() => {
            const totalItems = localSchoolData.pagination?.total;
            const totalPages = Math.ceil(totalItems / itemsPerRow);

            return totalPages > 1 ? (
              <div className="mt-6 flex justify-center items-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : null;
          })()}
        </>
      )}
    </div>
  );
}

export default SchoolList;
